import { Archive, Globe, Plus, Table2 } from 'lucide-react';
import type { router as AppRouter } from '../../router';
import { registerCommands, type Command } from '../../shell/command-registry';
import { ALL_SCOPE, useSession } from '../../stores/session';

/**
 * S5's palette entries, registered from this module rather than by editing
 * `shell-commands.ts` — 06-qol §1's registration pattern, and the reason it exists.
 *
 * Every one names the §L23 object it needs, so a panelist never sees an org
 * destination they would be refused at. The filtering happens before display, not
 * after a click.
 *
 * ORG-01 is not here: the module bar already registers "Organisation" as a
 * destination, and the Navigate section is capped at eight — a second entry for the
 * same screen pushes a different one off the list for no gain.
 */
export function registerOrganisationCommands(router: typeof AppRouter): () => void {
  const commands: Command[] = [
    {
      id: 'org.ledger',
      label: 'Position ledger',
      section: 'navigate',
      context: 'Screen · ORG-02',
      permission: 'org_unit_position',
      icon: Table2,
      keywords: ['positions', 'seats', 'vacancies', 'ledger'],
      run: () => void router.navigate({ to: '/organisation/positions' }),
    },
    {
      id: 'org.headcount',
      label: 'Headcount plan',
      section: 'navigate',
      context: 'Screen · ORG-04',
      permission: 'headcount_plan',
      icon: Table2,
      keywords: ['plan', 'quarter', 'forecast', 'budget'],
      run: () => void router.navigate({ to: '/organisation/headcount' }),
    },
    {
      id: 'org.createPosition',
      label: 'Add a position',
      section: 'action',
      context: 'Organisation',
      permission: 'org_unit_position',
      icon: Plus,
      keywords: ['seat', 'new', 'create', 'budget'],
      // The ledger owns the dialog, so the command takes you there rather than
      // opening a modal over whatever screen you happened to be on.
      run: () => void router.navigate({ to: '/organisation/positions', search: { create: '1' as const } }),
    },
    {
      id: 'org.showArchived',
      label: 'Show archived positions',
      section: 'action',
      context: 'Organisation',
      permission: 'org_unit_position',
      icon: Archive,
      keywords: ['archive', 'retired', 'history', 'closed'],
      run: () =>
        void router.navigate({ to: '/organisation/positions', search: { archived: '1' } }),
    },
    {
      id: 'org.clearScope',
      label: 'Show every venture',
      section: 'action',
      context: 'Scope',
      icon: Globe,
      keywords: ['scope', 'filter', 'clear', 'all', 'unit'],
      // Offered whatever the current scope is: somebody who cannot remember how
      // they narrowed the application needs one obvious way back, and hiding the
      // command until it applies means searching for something that is not there.
      run: () => useSession.getState().setScope(ALL_SCOPE),
    },
  ];

  return registerCommands(commands);
}
